import React, { PropsWithChildren } from 'react';
import classNames from 'classnames';
import { NativeProps } from '@/src/components/interfaces';
import { AlignItems, CFlex, JustifyContent } from '@/src/components/c-flex/CFlex';
import { CLoader, CLoaderColor, CLoaderSize } from '@/src/components/c-loader/CLoader';
import { CText, CTextSize } from '@/src/components/c-text/CText';
import { CIcon, CIcons } from '@/src/components/c-icon/CIcon';

export enum CButtonPattern {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
}

export enum CButtonColor {
  Accent = 'accent',
  Accent1 = 'accent-1',
  Accent2 = 'accent-2',
  Accent5 = 'accent-5',
  Accent6 = 'accent-6',
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

export enum CButtonSize {
  Sm = 'sm',
  Md = 'md',
  Lg = 'lg',
}

export enum CButtonShape {
  Rounded = 'rounded',
  Square = 'square',
}

export enum CButtonType {
  Submit = 'submit',
  Reset = 'reset',
  Button = 'button'
}

export interface CButtonProps extends NativeProps {
  pattern?: CButtonPattern;
  color?: CButtonColor;
  text?: string;
  icon?: CIcons;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  type?: CButtonType;
  shape?: CButtonShape;
  size?: CButtonSize;
}

export function CButton(props: PropsWithChildren<CButtonProps>): React.JSX.Element {
  const pattern = props.pattern ?? CButtonPattern.Primary;
  const color = props.color ?? CButtonColor.Accent;
  const shape = props.shape ?? CButtonShape.Rounded;
  const size = props.size ?? CButtonSize.Md;

  return (
    <button
      id={props.id}
      type={props.type ?? CButtonType.Button}
      disabled={props.disabled}
      style={props.style}
      onClick={props.onClick}
      onKeyUp={props.onKeyUp}
      onKeyDown={props.onKeyDown}
      className={classNames(
        'tap--disabled',
        'c-button',
        `c-button--pattern-${pattern}`,
        `c-button--color-${color}`,
        `c-button--shape-${shape}`,
        `c-button--size-${size}`,
        {
          'c-button--loading': props.loading,
          'c-button--disabled': props.disabled,
          'c-button--full-width': props.fullWidth,
        },
        props.class,
      )}
      data-test-selector={props.testSelector ?? 'c-button'}
    >
      <CFlex class="height--percentage-100 width--percentage-100 c-gap--sm" justifyContent={JustifyContent.Center} alignItems={AlignItems.Center}>
        {props.loading && (
          <CLoader size={CLoaderSize.Button} color={pattern === CButtonPattern.Primary ? CLoaderColor.White : props.disabled ? CLoaderColor.Accent4 : CLoaderColor.Accent6}/>
        )}
        {!props.loading && props.icon && (
          <CIcon icon={props.icon} data-test-selector="icon"/>
        )}
        {!props.loading && props.text && (
          <CText size={CTextSize.Lg} data-test-selector="text">
            {props.text}
          </CText>
        )}
      </CFlex>
    </button>
  );
}